/* start - flights_split_bounds_us */
import React, { useCallback } from "react";
import SearchResultsWrapperDesktop from "./SearchResultsWrapper.desktop";
import { HiddenVisually, Title } from "@bookingcom/bui-react";
import DSABanner from "../DSABanner";
import SearchResultBanner from "../SearchResultBanner/SearchResultBanner";
import useSearchResultsDesktopController from "./useSearchResults.desktop.controller";
import SkeletonSearchDesktop, { SkeletonSearchContent } from "../Skeleton/SkeletonPages/SkeletonSearch.desktop";
import FailedSearchResults from "./FailedSearchResults.desktop";
import { RemoveDirectFlights } from "./components/RemoveDirectFlights";
import { TooManyFilters } from "./components/TooManyFilters";
import { NoResultsFound } from "./components/NoResultsFound";
import SearchResultsContentDesktop from "./SearchResultsContent.desktop";
import SearchResultsListOutboundDesktop from "./SearchResultsList.outbound.desktop";
import { UIFlightData } from "@flights/types";
import useCreateUrl from "hooks/useCreateUrl";
import { useHistory, useParams } from "react-router-dom";
import { getLocationParams } from "./utils";
import { t, useI18n } from "@bookingcom/lingojs-react";
import Frame from "../Frame";
import NoDirectFlightsBanner from "./components/NoDirectFlightsBanner";
import { DestinationUkraineWarning } from "../TravelAlert";
import SirfInfoBanner from "../SirfInfoBanner";
import flights_split_bounds_us from "utils/experiments/funnel/flights_split_bounds_us";

const SearchResultsOutboundDesktop = () => {
  const {
    getScreenReaderAnnouncementText,
    appliedFilters,
    isFirstLoad,
    canExtendSearch,
    onExpandToCitySearch,
    hasFlights,
    canRemoveDirectFlightsFilter,
    shouldMoveBudgetFilter,
    flights,
    fetchStatus,
    banners,
    isSirfAvailable,
    fetchedAt
  } = useSearchResultsDesktopController();
  const i18n = useI18n();

  const humanRightBanner = banners?.find((banner) => banner.id === "LEGAL_NOTICE_HUMAN_RIGHTS");

  const { createUrl } = useCreateUrl();
  const history = useHistory();
  const { airports } = useParams<{ airports: string }>();

  const onClickSelectFlight = useCallback(
    async (flight: UIFlightData) => {
      flights_split_bounds_us.goals.cta_click();
      const params = resetSortAndPageParams(getLocationParams(location.search));
      const url = createUrl(`/flights/${airports}/inbound/${flight.outboundFlightInfo?.token}/?${params}`);
      history.push(url);
    },
    [createUrl, airports, history]
  );

  let resultingComp = undefined;

  if (fetchStatus === "initial" || fetchStatus === "loading") {
    if (isFirstLoad) {
      // this is the result of SSR
      return <SkeletonSearchDesktop />;
    }
    // need sidebar from now on and no SSR
    resultingComp = <SkeletonSearchContent />;
  }

  if (fetchStatus === "failed" && canExtendSearch) {
    // this can also be SSR
    return <FailedSearchResults onClickExpandToCitySearch={onExpandToCitySearch} />;
  }

  // case of success should go here, i.e. the composed result
  if (fetchStatus === "success") {
    if (hasFlights) {
      // No SSR for this case, why not ? because api is called from client side
      resultingComp = (
        <SearchResultsContentDesktop flights={flights} fetchedAt={fetchedAt}>
          <NoDirectFlightsBanner />
          <DestinationUkraineWarning />
          {isSirfAvailable && (
            <SirfInfoBanner
              bannerName="search-results"
              bannerContent={i18n.trans(t("flights_sr_spanish_discount_banner"))}
            />
          )}
          <SearchResultsListOutboundDesktop flights={flights} onSelectFlight={onClickSelectFlight} />
        </SearchResultsContentDesktop>
      );
    } else if (canRemoveDirectFlightsFilter) {
      // can be generated from SSR
      return (
        <>
          <SearchResultBanner banner={humanRightBanner} />
          <RemoveDirectFlights />
        </>
      );
    }
  }

  if (!resultingComp) {
    // this is a fallthrough default when everything else fails
    if (appliedFilters.length > 0) {
      // need sidebar here too
      resultingComp = <TooManyFilters />;
    } else {
      return <NoResultsFound />;
    }
  }

  return (
    <SearchResultsWrapperDesktop
      banners={
        <>
          {humanRightBanner && <SearchResultBanner banner={humanRightBanner} />}
          <DSABanner />
          <Frame pb={4}>
            <Title
              title={i18n.trans(t("flights_sr_split_bounds_us_select_departing"))}
              subtitle={i18n.trans(t("flights_sr_split_bounds_us_price_disclaimer"))}
            />
          </Frame>
        </>
      }
      shouldMoveBudgetFilter={shouldMoveBudgetFilter}
    >
      {resultingComp}

      <HiddenVisually>
        <div aria-live="polite">{getScreenReaderAnnouncementText()}</div>
      </HiddenVisually>
    </SearchResultsWrapperDesktop>
  );
};

const resetSortAndPageParams = (params: URLSearchParams) => {
  params.set("sort", "BEST");
  params.set("page", "1");

  return params;
};

export default React.memo(SearchResultsOutboundDesktop);
/* end - flights_split_bounds_us */
